import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser, signOut } from '@/store/authSlice';
import { isDevEnvironment, setDebugEnabled, useDebugEnabled } from '@/debug/debugPreference';
import { setSoundEnabled, unlockAudio, useSoundEnabled } from '@/audio/rushAudio';
import type { AppDispatch } from '@/store';
import './ProfileMenu.css';

export function ProfileMenu() {
  const user = useSelector(selectUser);
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState(false);
  const debugOn = useDebugEnabled();
  const soundOn = useSoundEnabled();
  const showDebug = isDevEnvironment();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = user?.name || user?.username || 'Player';

  return (
    <div className="profile-menu" ref={rootRef}>
      <button
        type="button"
        className="profile-menu-button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        Profile
      </button>
      {open && (
        <div className="profile-popup" role="menu">
          <div className="profile-identity">
            <div className="profile-name">{name}</div>
            {user?.email && <div className="profile-email">{user.email}</div>}
          </div>
          {showDebug && (
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={debugOn}
              className="profile-item"
              onClick={() => setDebugEnabled(!debugOn)}
            >
              <span>Debug</span>
              <span className="profile-item-state">{debugOn ? 'On' : 'Off'}</span>
            </button>
          )}
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={soundOn}
            className="profile-item"
            onClick={() => {
              const next = !soundOn;
              setSoundEnabled(next);
              if (next) unlockAudio();
            }}
          >
            <span>Sound</span>
            <span className="profile-item-state">{soundOn ? 'On' : 'Off'}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="profile-item profile-sign-out"
            onClick={() => {
              setOpen(false);
              dispatch(signOut());
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
